import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { addDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import {
  CalendarRange,
  Clock,
  CalendarOff,
  SlidersHorizontal,
  Plus,
  X,
  CheckCircle2,
  CircleSlash,
} from "lucide-react";
import {
  getBookingSettings,
  updateBookingSettings,
  getAllBookings,
} from "../../../services/api";
import { BookingSettings, DayRule } from "../../../types/booking";
import { HALIFAX_TZ, toHalifaxDateString } from "../../../lib/timezone";
import { cn } from "../../../lib/util";
import { ToastProvider } from "../../../components/providers/toast-provider";

const DAYS: { key: string; label: string; short: string }[] = [
  { key: "monday", label: "Monday", short: "Mon" },
  { key: "tuesday", label: "Tuesday", short: "Tue" },
  { key: "wednesday", label: "Wednesday", short: "Wed" },
  { key: "thursday", label: "Thursday", short: "Thu" },
  { key: "friday", label: "Friday", short: "Fri" },
  { key: "saturday", label: "Saturday", short: "Sat" },
  { key: "sunday", label: "Sunday", short: "Sun" },
];

const TABS = [
  { id: "overview", label: "Overview", icon: CalendarRange },
  { id: "hours", label: "Weekly hours", icon: Clock },
  { id: "time-off", label: "Time off", icon: CalendarOff },
  { id: "rules", label: "Booking rules", icon: SlidersHorizontal },
] as const;

type TabId = (typeof TABS)[number]["id"];

// ── Helpers ────────────────────────────────────────────────────────────────
function halifaxDayKey(date: Date): string {
  return formatInTimeZone(date, HALIFAX_TZ, "EEEE").toLowerCase();
}
function fmt(date: Date, pattern: string): string {
  return formatInTimeZone(date, HALIFAX_TZ, pattern);
}

export default function SchedulePage() {
  const [params, setParams] = useSearchParams();
  const tabParam = (params.get("tab") as TabId) || "overview";
  const activeTab: TabId = TABS.some((t) => t.id === tabParam) ? tabParam : "overview";

  const [settings, setSettings] = useState<BookingSettings | null>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newClosure, setNewClosure] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([getBookingSettings(), getAllBookings()])
      .then(([s, b]: any) => {
        if (!active) return;
        setSettings(s.data?.data ?? null);
        setBookings(b?.data?.data ?? []);
      })
      .catch(() => toast.error("Could not load your schedule."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const setTab = (id: TabId) => setParams({ tab: id }, { replace: true });

  const updateDay = (key: string, patch: Partial<DayRule>) => {
    if (!settings) return;
    setSettings({
      ...settings,
      working_hours: {
        ...settings.working_hours,
        [key]: { ...settings.working_hours[key], ...patch },
      },
    });
  };

  const addClosure = () => {
    if (!settings || !newClosure) return;
    if (!settings.closures.includes(newClosure)) {
      setSettings({ ...settings, closures: [...settings.closures, newClosure].sort() });
    }
    setNewClosure("");
  };

  const removeClosure = (date: string) => {
    if (!settings) return;
    setSettings({ ...settings, closures: settings.closures.filter((d) => d !== date) });
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await updateBookingSettings({
        working_hours: settings.working_hours,
        slot_interval_minutes: settings.slot_interval_minutes,
        buffer_minutes: settings.buffer_minutes,
        lead_time_hours: settings.lead_time_hours,
        closures: settings.closures,
      });
      toast.success("Schedule updated. The public booking page reflects it now.");
    } catch (error: any) {
      toast.error(
        error?.response?.status === 422
          ? "Please check your hours — closing time must be after opening time."
          : "Could not save your schedule."
      );
    } finally {
      setSaving(false);
    }
  };

  // Next 21 days, with open/closed state and booking counts.
  const bookingsByDate = useMemo(() => {
    const m = new Map<string, number>();
    bookings.forEach((b) => {
      if (b.status === "declined" || !b.booking_start_date_time) return;
      const d = toHalifaxDateString(b.booking_start_date_time);
      m.set(d, (m.get(d) ?? 0) + 1);
    });
    return m;
  }, [bookings]);

  const upcomingDays = useMemo(() => {
    if (!settings) return [];
    const today = new Date();
    return Array.from({ length: 21 }, (_, i) => {
      const date = addDays(today, i);
      const iso = toHalifaxDateString(date);
      const rule = settings.working_hours[halifaxDayKey(date)];
      const isClosure = settings.closures.includes(iso);
      const closed = isClosure || !rule || rule.closed;
      return {
        date,
        iso,
        weekday: fmt(date, "EEE"),
        dayNum: fmt(date, "d"),
        month: fmt(date, "MMM"),
        closed,
        isClosure,
        open: rule?.open,
        close: rule?.close,
        count: bookingsByDate.get(iso) ?? 0,
        isToday: i === 0,
      };
    });
  }, [settings, bookingsByDate]);

  if (loading || !settings) {
    return (
      <>
        <ToastProvider />
        <div className="flex items-center gap-3 py-20 text-[#9A6B69]">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#EAD7D6] border-t-[#B87775]" />
          Loading your schedule…
        </div>
      </>
    );
  }

  const showSave = activeTab !== "overview";

  return (
    <div className="mx-auto max-w-5xl">
      <ToastProvider />
      {/* Page head */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#B87775]">Operations</p>
          <h1 className="font-serif text-3xl text-[#3a2e2c]">Schedule</h1>
          <p className="mt-1 text-sm text-[#8a7a78]">
            Your hours, time off and booking rules — all in Atlantic Time, applied
            to the public booking page the moment you save.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setTab("time-off")}
          className="flex h-11 items-center gap-2 rounded-full bg-[#B87775] px-5 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
        >
          <CalendarOff className="h-4 w-4" />
          Block a day off
        </button>
      </div>

      {/* Tabs */}
      <div
        role="tablist"
        aria-label="Schedule sections"
        className="mb-6 flex flex-wrap gap-1 rounded-full border border-[#EAD7D6] bg-white p-1"
      >
        {TABS.map((t) => {
          const selected = activeTab === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={selected}
              onClick={() => setTab(t.id)}
              className={cn(
                "flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-full px-4 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]",
                selected ? "bg-[#B87775] text-white" : "text-[#8a5a58] hover:bg-[#FBEFEF]"
              )}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── Overview ─────────────────────────────────────────────────── */}
      {activeTab === "overview" && (
        <section className="animate-charme-rise" aria-label="Next three weeks">
          <div className="mb-3 flex items-center gap-4 text-xs text-[#9A6B69]">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#B87775]" /> Open
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#EAD7D6]" /> Closed
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#FBEFEF] text-[9px] font-semibold text-[#B87775]">
                2
              </span>
              Bookings that day
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-7">
            {upcomingDays.map((d) => (
              <div
                key={d.iso}
                className={cn(
                  "relative flex flex-col rounded-xl border p-3 transition",
                  d.closed
                    ? "border-[#EAD7D6] bg-[#F7F0EF]"
                    : "border-[#EAD7D6] bg-white hover:border-[#B87775]",
                  d.isToday && "ring-2 ring-[#B87775] ring-offset-2"
                )}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-[11px] uppercase tracking-wide text-[#9A6B69]">
                    {d.weekday}
                  </span>
                  {d.count > 0 && (
                    <span
                      className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#FBEFEF] px-1 text-[11px] font-semibold tabular-nums text-[#B87775]"
                      title={`${d.count} booking${d.count > 1 ? "s" : ""}`}
                    >
                      {d.count}
                    </span>
                  )}
                </div>
                <span className="mt-0.5 font-serif text-2xl tabular-nums text-[#3a2e2c]">
                  {d.dayNum}
                </span>
                <span className="text-[11px] text-[#9A6B69]">{d.month}</span>
                <div className="mt-2 flex items-center gap-1 text-[11px]">
                  {d.closed ? (
                    <span className="inline-flex items-center gap-1 text-[#9A6B69]">
                      <CircleSlash className="h-3 w-3" />
                      {d.isClosure ? "Day off" : "Closed"}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 tabular-nums text-[#8a5a58]">
                      <span className="h-2 w-2 rounded-full bg-[#B87775]" />
                      {d.open}–{d.close}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Weekly hours ─────────────────────────────────────────────── */}
      {activeTab === "hours" && (
        <section className="animate-charme-rise space-y-2.5" aria-label="Recurring weekly hours">
          {DAYS.map(({ key, label }) => {
            const rule = settings.working_hours[key] ?? { open: "10:00", close: "19:00", closed: true };
            const open = !rule.closed;
            return (
              <div
                key={key}
                className={cn(
                  "flex flex-wrap items-center gap-4 rounded-xl border px-4 py-3.5 transition",
                  open ? "border-[#EAD7D6] bg-white" : "border-[#EAD7D6] bg-[#F7F0EF]"
                )}
              >
                <span className="w-28 font-serif text-lg text-[#3a2e2c]">{label}</span>

                <button
                  type="button"
                  role="switch"
                  aria-checked={open}
                  onClick={() => updateDay(key, { closed: open })}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2",
                    open ? "bg-[#B87775]" : "bg-[#D9C4C3]"
                  )}
                >
                  <span className="sr-only">{open ? "Open" : "Closed"} on {label}</span>
                  <span
                    className={cn(
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
                      open ? "translate-x-5" : "translate-x-0.5"
                    )}
                  />
                </button>

                {open ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={rule.open ?? "10:00"}
                      onChange={(e) => updateDay(key, { open: e.target.value })}
                      className="h-11 rounded-lg border border-[#E3C9C8] bg-white px-3 text-sm text-[#3a2e2c] tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
                    />
                    <span className="text-sm text-[#9A6B69]">to</span>
                    <input
                      type="time"
                      value={rule.close ?? "19:00"}
                      onChange={(e) => updateDay(key, { close: e.target.value })}
                      className="h-11 rounded-lg border border-[#E3C9C8] bg-white px-3 text-sm text-[#3a2e2c] tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
                    />
                  </div>
                ) : (
                  <span className="text-sm italic text-[#9A6B69]">Closed all day</span>
                )}
              </div>
            );
          })}
        </section>
      )}

      {/* ── Time off ─────────────────────────────────────────────────── */}
      {activeTab === "time-off" && (
        <section className="animate-charme-rise space-y-5" aria-label="Time off and holidays">
          <div className="rounded-xl border border-[#EAD7D6] bg-white p-5">
            <h2 className="font-serif text-xl text-[#3a2e2c]">Block a date</h2>
            <p className="mt-1 text-sm text-[#8a7a78]">
              Close a single day for a holiday, time off, or a personal commitment.
              Blocked days disappear from the public booking calendar straight away.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={newClosure}
                min={toHalifaxDateString(new Date())}
                onChange={(e) => setNewClosure(e.target.value)}
                className="h-11 rounded-lg border border-[#E3C9C8] bg-white px-3 text-sm text-[#3a2e2c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
              />
              <button
                type="button"
                onClick={addClosure}
                disabled={!newClosure}
                className="flex h-11 items-center gap-1.5 rounded-full border border-[#B87775] px-4 text-sm font-medium text-[#B87775] transition hover:bg-[#FBEFEF] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
              >
                <Plus className="h-4 w-4" />
                Block this date
              </button>
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-xs uppercase tracking-[0.2em] text-[#B87775]">
              Blocked dates
            </h3>
            {settings.closures.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#E3C9C8] bg-white px-5 py-10 text-center">
                <CalendarOff className="mx-auto h-8 w-8 text-[#D9C4C3]" />
                <p className="mt-2 text-sm text-[#8a7a78]">
                  No days off scheduled. Your weekly hours apply on every open day.
                </p>
              </div>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {settings.closures.map((date) => (
                  <li
                    key={date}
                    className="inline-flex items-center gap-2 rounded-full bg-[#FBEFEF] py-1.5 pl-4 pr-2 text-sm text-[#8a5a58]"
                  >
                    <span className="tabular-nums">
                      {formatInTimeZone(new Date(date + "T12:00:00"), HALIFAX_TZ, "EEE, d MMM yyyy")}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeClosure(date)}
                      aria-label={`Remove block on ${date}`}
                      className="flex h-7 w-7 items-center justify-center rounded-full text-[#B87775] transition hover:bg-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-4 text-xs text-[#9A6B69]">
              Need to block part of a day? Ad-hoc, within-day blocks are managed in
              your connected Google Calendar and are respected automatically.
            </p>
          </div>
        </section>
      )}

      {/* ── Booking rules ────────────────────────────────────────────── */}
      {activeTab === "rules" && (
        <section className="animate-charme-rise grid gap-4 sm:grid-cols-3" aria-label="Booking rules">
          <RuleCard
            label="Slot interval"
            unit="minutes"
            help="How frequently start times are offered."
            value={settings.slot_interval_minutes}
            min={5}
            step={5}
            onChange={(v) => setSettings({ ...settings, slot_interval_minutes: v })}
          />
          <RuleCard
            label="Buffer after booking"
            unit="minutes"
            help="Quiet time to reset between clients."
            value={settings.buffer_minutes}
            min={0}
            step={5}
            onChange={(v) => setSettings({ ...settings, buffer_minutes: v })}
          />
          <RuleCard
            label="Minimum notice"
            unit="hours"
            help="How far ahead a client must book."
            value={settings.lead_time_hours}
            min={0}
            step={1}
            onChange={(v) => setSettings({ ...settings, lead_time_hours: v })}
          />
        </section>
      )}

      {/* Sticky save bar for the editable tabs */}
      {showSave && (
        <div className="sticky bottom-4 mt-6 flex items-center justify-between gap-4 rounded-xl border border-[#EAD7D6] bg-white/95 px-5 py-3 shadow-sm backdrop-blur lg:bottom-0">
          <span className="flex items-center gap-2 text-sm text-[#8a7a78]">
            <CheckCircle2 className="h-4 w-4 text-[#B87775]" />
            Changes apply to the public booking page the moment you save.
          </span>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex h-11 items-center rounded-full bg-[#B87775] px-6 text-sm font-medium text-white transition hover:bg-[#a96764] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}

function RuleCard({
  label,
  unit,
  help,
  value,
  min,
  step,
  onChange,
}: {
  label: string;
  unit: string;
  help: string;
  value: number;
  min: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="rounded-xl border border-[#EAD7D6] bg-white p-5">
      <p className="font-serif text-lg text-[#3a2e2c]">{label}</p>
      <p className="mt-0.5 text-sm text-[#8a7a78]">{help}</p>
      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          onClick={() => onChange(Math.max(min, value - step))}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E3C9C8] text-lg text-[#B87775] transition hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
        >
          −
        </button>
        <div className="flex flex-1 flex-col items-center">
          <input
            type="number"
            min={min}
            step={step}
            value={value}
            onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
            className="w-full rounded-lg border border-[#E3C9C8] bg-white py-2 text-center font-serif text-2xl tabular-nums text-[#3a2e2c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
          />
          <span className="mt-1 text-xs uppercase tracking-wide text-[#9A6B69]">{unit}</span>
        </div>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          onClick={() => onChange(value + step)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E3C9C8] text-lg text-[#B87775] transition hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
        >
          +
        </button>
      </div>
    </div>
  );
}
